import { Portal } from '@angular/cdk/portal';
import { Injectable, signal } from '@angular/core';
import { ExtendedFileMetadata } from '@lib/models/extended-file-metadata';
import { OuiSlidePanelService } from 'omnium-ui/layout';
import { Observable, filter, map, tap } from 'rxjs';

/**
 * Fichier affiché par le panneau : un document GED, ou un aperçu généré à la volée dont seuls l'URL (SAS du fichier
 * temporaire), le nom et le libellé sont connus.
 */
export type PanelFile = Partial<ExtendedFileMetadata> & Pick<ExtendedFileMetadata, 'typeDocumentLabel'>;

@Injectable({ providedIn: 'root' })
export class FilePanelService {
  public fileMetadata = signal<PanelFile | null>(null);
  public readonly viewerClosed$: Observable<void>;
  private opened: boolean = false;
  private fileViewerPortal: Portal<any> | null = null;
  public displayDelete = signal<boolean>(false);

  constructor(private slidePanelService: OuiSlidePanelService) {
    this.viewerClosed$ = this.slidePanelService.slidePanelState$.pipe(
      filter(state => state === 'closed' && this.opened),
      tap(() => this.onPanelClose()),
      map(() => {})
    );
  }

  public registerPortal(portal: Portal<any>) {
    this.fileViewerPortal = portal;
  }

  public openFileViewer(fileMetadata: PanelFile, displayDelete: boolean = false) {
    if (!this.fileViewerPortal) {
      throw new Error(`No portal registered in document viewer service. Please register a portal using registerPortal.`);
    }
    this.fileMetadata.set(fileMetadata);
    this.displayDelete.set(displayDelete);
    this.slidePanelService.setSlidePanelContent(this.fileViewerPortal);
    this.slidePanelService.openSlidePanel({ position: 'right', autoFocus: false, disableClose: false });

    this.opened = true;
  }

  public closeFileViewer() {
    this.slidePanelService.setSlidePanelContent(null);
    this.slidePanelService.closeSlidePanel();
  }

  private onPanelClose() {
    this.opened = false;
    this.displayDelete.set(false);
  }
}
